import { useCallback, useEffect, useRef, useState } from 'react'

import { format } from 'date-fns'
import { type DayPickerProps } from 'react-day-picker'

import { DateCalendar } from './DateCalendar'
import { IconCalendar } from '../Icon'
import { CalendarIconStyled } from './styles/CalendarIconStyled'
import { DatePickerInputStyled } from './styles/DatePickerInputStyled'
import { DatePickerPopoverStyled } from './styles/DatePickerPopoverStyled'
import { SingleDatePickerStyled } from './styles/SingleDatePickerStyled'
import type {
  DateChangeSource,
  DateValue,
  SingleDatePickerPreset,
} from './types'
import { parseDateInput } from './utils/parseDateInput'
import { getAnalyticsDataAttrs } from '@ui/utils/analytics'

export interface ISingleDatePickerProps {
  /** Currently selected date */
  date?: DateValue
  /** Called when a date is selected */
  onDateChange?: (date: Date, source?: DateChangeSource) => void
  /** Whether the picker popover is open */
  focused?: boolean
  /** Called when focus state changes */
  onFocusChange?: (arg: { focused: boolean }) => void
  /** Unique identifier */
  id: string
  /** Placeholder text for the input */
  placeholder?: string
  /** Whether the picker is disabled */
  disabled?: boolean
  /** Whether a value is required */
  required?: boolean
  /** Whether the input should take full width */
  block?: boolean
  /** Show error styling */
  error?: boolean
  /** Function to disable specific dates */
  isOutsideRange?: (day: Date) => boolean
  /** Calendar popover alignment */
  anchorDirection?: 'left' | 'right'
  /** Analytics tracking identifier */
  analyticsId?: string
  /** Array of preset date options */
  presets?: SingleDatePickerPreset[]
  /** Title for the presets section */
  presetTitle?: string
  /** Called when a preset is selected */
  onPresetChange?: (preset: SingleDatePickerPreset) => void
  /** Earliest selectable month in month/year dropdown */
  fromMonth?: Date
  /** Latest selectable month in month/year dropdown */
  toMonth?: Date
  /** Locale for formatting and calendar display */
  locale?: DayPickerProps['locale']
  /** Initial month shown */
  defaultMonth?: Date
}

export const SingleDatePicker = ({
  date = null,
  onDateChange = () => undefined,
  focused = false,
  onFocusChange = () => undefined,
  id,
  placeholder = 'Select date',
  disabled = false,
  required = false,
  block = false,
  error = false,
  isOutsideRange,
  anchorDirection = 'left',
  analyticsId,
  presets,
  presetTitle,
  onPresetChange = () => undefined,
  fromMonth,
  toMonth,
  locale,
  defaultMonth,
}: ISingleDatePickerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [month, setMonth] = useState<Date>(date ?? new Date())
  const [inputValue, setInputValue] = useState<string>(
    date ? format(date, 'P') : ''
  )

  const inputRef = useRef(null)

  useEffect(() => {
    if (date) {
      setMonth(date)
      setInputValue(format(date, 'P'))
    }
  }, [date])

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onFocusChange({ focused: false })
      }
    },
    [onFocusChange]
  )

  const handleEscapeKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onFocusChange({ focused: false })
      }
    },
    [onFocusChange]
  )

  useEffect(() => {
    if (focused) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [focused, handleClickOutside, handleEscapeKey])

  const handleDaySelect = (day: Date | undefined) => {
    if (day) {
      onDateChange(day, 'calendar')
      onFocusChange({ focused: false })
      setInputValue(format(day, 'P'))
    }
  }

  const handleInputChange = (e) => {
    setInputValue(e.target.value)

    const parsedDate = parseDateInput(e.target.value)

    if (parsedDate) {
      onDateChange(parsedDate)
      setMonth(parsedDate)
    } else {
      onDateChange(undefined)
    }
  }

  const handlePresetClick = (preset: { label: string }) => {
    const typedPreset = preset as SingleDatePickerPreset
    onDateChange(typedPreset.date, 'preset')
    onPresetChange(typedPreset)
    onFocusChange({ focused: false })
  }

  const handleInputClick = () => {
    if (!disabled) {
      inputRef.current.focus()
    }
  }

  const analyticsProps = getAnalyticsDataAttrs(analyticsId)

  return (
    <SingleDatePickerStyled
      ref={containerRef}
      block={block}
      {...analyticsProps}
    >
      <DatePickerInputStyled
        error={error}
        disabled={disabled}
        focused={focused}
        block={block}
        onClick={handleInputClick}
      >
        <input
          id={id}
          ref={inputRef}
          onChange={handleInputChange}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          value={inputValue}
          aria-label={placeholder}
          onFocus={() => onFocusChange({ focused: true })}
        />
        <CalendarIconStyled>
          <IconCalendar color='textPrimary' ariaLabel='Open calendar' />
        </CalendarIconStyled>
      </DatePickerInputStyled>

      {focused && (
        <DatePickerPopoverStyled anchorDirection={anchorDirection}>
          <DateCalendar
            mode='single'
            selected={date ?? undefined}
            onSelect={handleDaySelect}
            month={month}
            onMonthChange={setMonth}
            disabled={isOutsideRange}
            numberOfMonths={1}
            locale={locale}
            fromMonth={fromMonth}
            toMonth={toMonth}
            presets={presets}
            presetTitle={presetTitle}
            onPresetClick={handlePresetClick}
            defaultMonth={defaultMonth}
          />
        </DatePickerPopoverStyled>
      )}
    </SingleDatePickerStyled>
  )
}

SingleDatePicker.displayName = 'SingleDatePicker'
